import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageSquareReply, Pencil, Star, Trash2 } from 'lucide-react';
import { ApiError, api } from '@/lib/api';
import { useAuth } from '@/hooks/useApp';
import { formatRelative } from '@/lib/format';
import { Button, EmptyState, Textarea } from '@/components/ui';
import { StarInput, StarRating } from '@/components/StarRating';
import type { ListingReview, RatingSummary, ReviewPage, SellerReview } from '@/lib/types';

function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase();
}

function Avatar({ name, url }: { name: string; url?: string | null }) {
  if (url) {
    return <img src={url} alt="" loading="lazy" className="size-9 shrink-0 rounded-full object-cover" />;
  }

  return (
    <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-accent-soft text-[11px] font-bold text-accent">
      {initials(name)}
    </span>
  );
}

/**
 * The average, and the shape behind it.
 *
 * An average alone hides the distribution: 4,0 from forty consistent fours and 4,0 from twenty
 * fives and twenty threes describe very different cars, and only the bars tell them apart.
 */
export function RatingSummaryPanel({
  summary,
  label,
}: {
  summary: RatingSummary;
  label: string;
}) {
  const bars = [
    { star: 5, count: summary.breakdown.five },
    { star: 4, count: summary.breakdown.four },
    { star: 3, count: summary.breakdown.three },
    { star: 2, count: summary.breakdown.two },
    { star: 1, count: summary.breakdown.one },
  ];

  return (
    <div className="flex flex-col gap-5 rounded-[var(--radius-panel)] border border-default bg-raised p-5 shadow-card sm:flex-row sm:items-center sm:gap-8">
      <div className="flex shrink-0 flex-col items-center gap-1.5 sm:w-36">
        <span className="font-display text-4xl font-bold text-body tabular-nums">
          {summary.average ? summary.average.toFixed(1).replace('.', ',') : '—'}
        </span>
        <StarRating value={summary.average ?? 0} size="md" />
        <span className="text-xs text-faint tabular-nums">
          {summary.count === 0 ? 'Još nema ocena' : `${summary.count} ${label}`}
        </span>
      </div>

      <div className="min-w-0 flex-1 space-y-1.5">
        {bars.map(({ star, count }, index) => {
          const share = summary.count === 0 ? 0 : (count / summary.count) * 100;

          return (
            <div key={star} className="flex items-center gap-2.5 text-xs text-faint">
              <span className="flex w-8 shrink-0 items-center gap-0.5 tabular-nums">
                {star}
                <Star size={10} className="fill-current" />
              </span>
              <span className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-sunken">
                {/*
                  Width animates from zero on mount, staggered down the rows, so the distribution
                  draws itself rather than appearing as a finished chart.
                */}
                <span
                  className="block h-full rounded-full bg-gold transition-[width] duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
                  style={{ width: `${share}%`, transitionDelay: `${index * 60}ms` }}
                />
              </span>
              <span className="w-7 shrink-0 text-right tabular-nums">{count}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface FormState {
  rating: number;
  comment: string;
}

function ReviewComposer({
  heading,
  hint,
  initial,
  onSubmit,
  onDelete,
  busy,
  error,
}: {
  heading: string;
  hint: string;
  initial?: FormState;
  onSubmit: (state: FormState) => void;
  onDelete?: () => void;
  busy: boolean;
  error?: string | null;
}) {
  const [rating, setRating] = useState(initial?.rating ?? 0);
  const [comment, setComment] = useState(initial?.comment ?? '');
  const [open, setOpen] = useState(!initial);

  // The composer is reused across listings and sellers, so it has to pick up a different existing
  // review when the page it sits on changes underneath it.
  useEffect(() => {
    setRating(initial?.rating ?? 0);
    setComment(initial?.comment ?? '');
    setOpen(!initial);
  }, [initial]);

  if (initial && !open) {
    return (
      <div className="animate-fade-in flex flex-wrap items-center gap-3 rounded-[var(--radius-panel)] border border-accent/25 bg-accent-soft/60 p-4">
        <StarRating value={initial.rating} size="md" />
        <span className="min-w-0 flex-1 truncate text-sm text-muted">
          {initial.comment || 'Ostavili ste ocenu bez komentara.'}
        </span>
        <div className="flex shrink-0 gap-2">
          <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
            <Pencil size={14} />
            Izmeni
          </Button>
          {onDelete && (
            <Button variant="danger" size="sm" onClick={onDelete} disabled={busy}>
              <Trash2 size={14} />
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (rating > 0) {
          onSubmit({ rating, comment });
        }
      }}
      className="animate-fade-in-up space-y-4 rounded-[var(--radius-panel)] border border-default bg-raised p-5 shadow-card"
    >
      <div>
        <h3 className="font-semibold text-body">{heading}</h3>
        <p className="mt-0.5 text-xs text-faint">{hint}</p>
      </div>

      <StarInput value={rating} onChange={setRating} disabled={busy} />

      <Textarea
        value={comment}
        onChange={(event) => setComment(event.target.value)}
        placeholder="Napišite nekoliko rečenica o svom iskustvu… (nije obavezno)"
        maxLength={2000}
        className="min-h-24"
        disabled={busy}
      />

      {error && <p className="animate-fade-in-down text-xs text-danger">{error}</p>}

      <div className="flex flex-wrap gap-2">
        <Button type="submit" loading={busy} disabled={rating === 0}>
          {initial ? 'Sačuvaj izmenu' : 'Objavi ocenu'}
        </Button>
        {initial && (
          <Button type="button" variant="ghost" onClick={() => setOpen(false)} disabled={busy}>
            Odustani
          </Button>
        )}
      </div>
    </form>
  );
}

function ReviewShell({
  author,
  rating,
  comment,
  createdAt,
  editedAt,
  index,
  children,
}: {
  author: { displayName: string; profilePhotoUrl?: string | null };
  rating: number;
  comment?: string | null;
  createdAt: string;
  editedAt?: string | null;
  index: number;
  children?: React.ReactNode;
}) {
  return (
    <li
      style={{ '--i': Math.min(index, 9) } as React.CSSProperties}
      className="animate-fade-in-up stagger rounded-[var(--radius-panel)] border border-default bg-raised p-4 shadow-card"
    >
      <div className="flex items-start gap-3">
        <Avatar name={author.displayName} url={author.profilePhotoUrl} />

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="truncate text-sm font-semibold text-body">{author.displayName}</span>
            <StarRating value={rating} size="xs" />
            <span className="text-[11px] text-faint">
              {formatRelative(createdAt)}
              {editedAt && ' · izmenjeno'}
            </span>
          </div>

          {comment && (
            <p className="mt-2 text-sm leading-relaxed whitespace-pre-wrap text-body">{comment}</p>
          )}

          {children}
        </div>
      </div>
    </li>
  );
}

/* --------------------------------------------------------------------------------------------- */
/* Reviews of a car                                                                               */
/* --------------------------------------------------------------------------------------------- */

export function ListingReviews({
  listingId,
  isOwnedByCaller,
}: {
  listingId: string;
  isOwnedByCaller: boolean;
}) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const key = ['listing-reviews', listingId];

  const { data } = useQuery({
    queryKey: key,
    queryFn: () => api<ReviewPage<ListingReview>>(`/api/listings/${listingId}/reviews?pageSize=20`),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: key });
    queryClient.invalidateQueries({ queryKey: ['listing'] });
  };

  const submit = useMutation({
    mutationFn: (state: FormState) =>
      api(`/api/listings/${listingId}/reviews/mine`, {
        method: 'PUT',
        body: { rating: state.rating, comment: state.comment || null },
      }),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Ocenjivanje nije uspelo.'),
  });

  const remove = useMutation({
    mutationFn: () => api(`/api/listings/${listingId}/reviews/mine`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });

  const mine = data?.mine ?? null;
  const others = (data?.items ?? []).filter((review) => !review.isOwnedByCaller);

  return (
    <section className="mt-10">
      <h2 className="mb-4 text-lg font-semibold text-body">Ocene vozila</h2>

      <div className="space-y-4">
        {data && <RatingSummaryPanel summary={data.summary} label="ocena" />}

        {user && !isOwnedByCaller && (
          <ReviewComposer
            heading={mine ? 'Vaša ocena' : 'Ocenite ovo vozilo'}
            hint="Ocenjujete konkretan automobil iz oglasa — stanje, opremljenost, da li odgovara opisu."
            initial={mine ? { rating: mine.rating, comment: mine.comment ?? '' } : undefined}
            onSubmit={(state) => submit.mutate(state)}
            onDelete={() => remove.mutate()}
            busy={submit.isPending || remove.isPending}
            error={error}
          />
        )}

        {!user && (
          <p className="rounded-[var(--radius-panel)] border border-dashed border-default px-4 py-6 text-center text-sm text-muted">
            <Link to="/prijava" className="font-medium text-accent hover:underline">
              Prijavite se
            </Link>{' '}
            da biste ocenili ovo vozilo.
          </p>
        )}

        {others.length > 0 ? (
          <ul className="space-y-3">
            {others.map((review, index) => (
              <ReviewShell
                key={review.id}
                author={review.author}
                rating={review.rating}
                comment={review.comment}
                createdAt={review.createdAt}
                editedAt={review.editedAt}
                index={index}
              />
            ))}
          </ul>
        ) : (
          data &&
          data.summary.count === 0 && (
            <p className="py-4 text-center text-sm text-faint">
              Ovo vozilo još nema ocena. Budite prvi.
            </p>
          )
        )}
      </div>
    </section>
  );
}

/* --------------------------------------------------------------------------------------------- */
/* Reviews of a seller                                                                            */
/* --------------------------------------------------------------------------------------------- */

function SellerReplyBox({
  review,
  onSaved,
}: {
  review: SellerReview;
  onSaved: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [reply, setReply] = useState(review.sellerReply ?? '');

  const save = useMutation({
    mutationFn: () =>
      api(`/api/sellers/reviews/${review.id}/reply`, { method: 'PUT', body: { reply } }),
    onSuccess: () => {
      setOpen(false);
      onSaved();
    },
  });

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="press mt-3 inline-flex items-center gap-1.5 rounded-lg text-xs font-medium text-muted transition hover:text-accent"
      >
        <MessageSquareReply size={13} />
        {review.sellerReply ? 'Izmeni odgovor' : 'Odgovori'}
      </button>
    );
  }

  return (
    <div className="animate-fade-in-down mt-3 space-y-2">
      <Textarea
        value={reply}
        onChange={(event) => setReply(event.target.value)}
        placeholder="Vaš odgovor…"
        maxLength={2000}
        className="min-h-20 text-sm"
      />
      <div className="flex gap-2">
        <Button size="sm" onClick={() => save.mutate()} loading={save.isPending}>
          Objavi odgovor
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
          Odustani
        </Button>
      </div>
    </div>
  );
}

export function SellerReviews({
  sellerId,
  listingId,
  compact = false,
}: {
  sellerId: string;
  /** When the reviewer arrived from a listing, the review is anchored to it. */
  listingId?: string;
  compact?: boolean;
}) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const key = ['seller-reviews', sellerId];

  const { data } = useQuery({
    queryKey: key,
    queryFn: () => api<ReviewPage<SellerReview>>(`/api/sellers/${sellerId}/reviews?pageSize=20`),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: key });
    queryClient.invalidateQueries({ queryKey: ['seller', sellerId] });
    queryClient.invalidateQueries({ queryKey: ['listing'] });
  };

  const submit = useMutation({
    mutationFn: (state: FormState) =>
      api(`/api/sellers/${sellerId}/reviews/mine`, {
        method: 'PUT',
        body: { rating: state.rating, comment: state.comment || null, listingId: listingId ?? null },
      }),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Ocenjivanje nije uspelo.'),
  });

  const remove = useMutation({
    mutationFn: () => api(`/api/sellers/${sellerId}/reviews/mine`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });

  const mine = data?.mine ?? null;
  const others = (data?.items ?? []).filter((review) => !review.isOwnedByCaller);
  const isSelf = user?.id === sellerId;

  return (
    <div className="space-y-4">
      {data && !compact && <RatingSummaryPanel summary={data.summary} label="ocena" />}

      {user && !isSelf && (
        <ReviewComposer
          heading={mine ? 'Vaša ocena prodavca' : 'Ocenite prodavca'}
          hint="Ocenjujete komunikaciju i poštenje — da li je opis odgovarao vozilu i kako je prošao dogovor."
          initial={mine ? { rating: mine.rating, comment: mine.comment ?? '' } : undefined}
          onSubmit={(state) => submit.mutate(state)}
          onDelete={() => remove.mutate()}
          busy={submit.isPending || remove.isPending}
          error={error}
        />
      )}

      {others.length > 0 ? (
        <ul className="space-y-3">
          {others.map((review, index) => (
            <ReviewShell
              key={review.id}
              author={review.author}
              rating={review.rating}
              comment={review.comment}
              createdAt={review.createdAt}
              editedAt={review.editedAt}
              index={index}
            >
              {review.listingShortCode && (
                <Link
                  to={`/oglas/${review.listingShortCode}`}
                  className="mt-2 inline-block text-[11px] text-faint transition hover:text-accent"
                >
                  povodom oglasa: {review.listingTitle}
                </Link>
              )}

              {review.sellerReply && (
                <div className="mt-3 rounded-xl border-l-2 border-accent bg-sunken px-3 py-2.5">
                  <p className="text-[11px] font-semibold text-accent">Odgovor prodavca</p>
                  <p className="mt-1 text-sm leading-relaxed whitespace-pre-wrap text-muted">
                    {review.sellerReply}
                  </p>
                </div>
              )}

              {review.canReply && <SellerReplyBox review={review} onSaved={invalidate} />}
            </ReviewShell>
          ))}
        </ul>
      ) : (
        data &&
        data.summary.count === 0 && (
          <EmptyState
            title="Nema ocena"
            description="Ovaj prodavac još nije ocenjen. Ako ste imali kontakt, vaša ocena pomaže sledećem kupcu."
          />
        )
      )}
    </div>
  );
}
